import { computed } from '@ember/object';
import BaseActualFilterAmplitude from '../../implementation-common/base-actual-filter-amplitude';
import * as CommonUtils from '../../../../utils/common';
import * as Complex from '../../../process-data/complex';
import FilterConstants from '../../../../filter-constants';

export default BaseActualFilterAmplitude.extend({
  components: null,
  frequency: null,
  implementation: null,
  whoAmI: 'app/models/implementation/deliyannis-friend/band-pass/actual-filter-amplitude.js',
  complexAlphaPlus: Complex.createComplex(0, 0),
  rx: computed('r1', 'r2', function () {
    var r1 = this.r1;
    var r2 = this.r2;
    var result = CommonUtils.parallel(r1, r2);
    return result;
  }),
  _R5RXCsqrSsqr: computed('rx', 'c1', 'r5', 'sSquared', function () {
    var rx = this.rx;
    var sSquared = this.sSquared;
    var r5 = this.r5;
    var c1 = this.c1;
    var result = r5 * rx * sSquared * c1 * c1;
    return result;
  }),
  complexROut: computed('opAmp', function () {
    var rOut = this.opAmp.rOut;
    var result = Complex.createComplex(rOut, 0);
    return result;
  }),
  complexBetaPlus: computed('r3', 'r4', 'sImaginary', function () {
    var r3 = this.r3;
    var r4 = this.r4;
    var sImaginary = this.sImaginary;
    var complexR3 = Complex.createComplex(r3, 0);
    var complexBetaDenom = Complex.createComplex(
      r3 + r4,
      r4 * r3 * FilterConstants.Limits.PARASITIC_INPUT_CAP * sImaginary,
    );
    var result = complexR3.divide(complexBetaDenom);

    return result;
  }),
  complexDenominator: computed('c1', 'r5', 'sImaginary', '_R5RXCsqrSsqr', 'rx', function () {
    var sImaginary = this.sImaginary;
    var R5RXCsqrSsqr = this._R5RXCsqrSsqr;
    var rx = this.rx;
    var c1 = this.c1;
    var r5 = this.r5;
    var twoRXCplusR5C = 2 * rx * c1 + r5 * c1;
    var result = Complex.createComplex(1 + R5RXCsqrSsqr, twoRXCplusR5C * sImaginary);
    return result;
  }),
  complexBetaMinus: computed('_R5RXCsqrSsqr', 'rx', 'c1', 'sImaginary', 'complexDenominator', function () {
    var R5RXCsqrSsqr = this._R5RXCsqrSsqr;
    var complexDenominator = this.complexDenominator;
    var rx = this.rx;
    var c1 = this.c1;
    var sImaginary = this.sImaginary;
    var complexBetaminusNumerator = Complex.createComplex(1 + R5RXCsqrSsqr, 2 * rx * c1 * sImaginary);
    var result = complexBetaminusNumerator.divide(complexDenominator);
    return result;
  }),
  complexRParallel: computed('rx', 'c1', 'r3', 'r4', 'r5', 'opAmp', 'sSquared', 'sImaginary', function () {
    var rx = this.rx;
    var sSquared = this.sSquared;
    var sImaginary = this.sImaginary;

    var complexZlookbackFront = Complex.createComplex(rx, 0);
    var r5 = this.r5;
    var c1 = this.c1;
    var complexZlookbackEndDenom = Complex.createComplex(r5 * c1 * c1 * sSquared, 2 * c1 * sImaginary);
    var complexZlookbackEndNum = Complex.createComplex(1, r5 * c1 * sImaginary);
    var complexZlookback = complexZlookbackFront.plus(complexZlookbackEndNum.divide(complexZlookbackEndDenom));

    var complexOne = Complex.createComplex(1, 0);
    var rOut = this.opAmp.rOut;
    var r3 = this.r3;
    var r4 = this.r4;
    var rParallelwithoutzlookback = CommonUtils.parallel(rOut, r3 + r4);
    var result = complexOne.divide(
      complexOne.divide(complexZlookback).plus(Complex.createComplex(1 / rParallelwithoutzlookback, 0)),
    );
    return result;
  }),
  complexFF: computed('complexRParallel', 'r1', function () {
    var complexRParallel = this.complexRParallel;
    var r1 = this.r1;
    var complexR1 = Complex.createComplex(r1, 0);
    var result = complexRParallel.divide(complexR1);
    return result;
  }),
  complexAlphaMinus: computed('sImaginary', 'complexDenominator', 'rx', 'c1', 'r1', 'r5', function () {
    var complexDenominator = this.complexDenominator;
    var sImaginary = this.sImaginary;
    var rx = this.rx;
    var r5 = this.r5;
    var c1 = this.c1;
    var r1 = this.r1;
    var alphaMinusReal = (r5 * c1 * rx) / r1;
    var result = Complex.createComplex(0, alphaMinusReal * sImaginary).divide(complexDenominator);
    return result;
  }),
  inputImpedance: computed('r1', 'r2', 'r5', 'c1', 'c2', 'sImaginary', 'complexAmplitude', function () {
    var r1 = this.r1;
    var r2 = this.r2;
    var r5 = this.r5;
    var c1 = this.c1;
    var c2 = this.c2;
    var sImaginary = this.sImaginary;
    var complexAmplitude = this.complexAmplitude;

    var complexOne = Complex.createComplex(1, 0);
    var complexC1s = Complex.createComplex(0, 0);
    complexC1s.imaginary = c1 * sImaginary;

    //This is the admittance for C1 in parallel with C2 + R5 in series
    let y2 = complexOne.divide(Complex.createComplex(r5, -1 / (c2 * sImaginary)));
    let y1 = complexC1s;
    let ySum = y1.plus(y2);

    //Find our junction voltage using the formula: vjunction = (v1y1 + v2y2 +v3y3)/(y1+y2+y3)
    //Since we're assuming vIn = 1 as our test voltage, voltage at the op amp output is complexAmplitude
    let vJunctionNum = Complex.complexify(1 / r1).plus(complexAmplitude.mult(ySum));
    let vJunctionDenom = Complex.complexify(1 / r1 + 1 / r2).plus(ySum);
    let vJunction = vJunctionNum.divide(vJunctionDenom);

    //Input current would be (vin - vJunction)/R1.  If we use vIn = 1 as our test voltage, then we can just flip to get input impedance.
    let result = Complex.complexify(r1).divide(complexOne.subs(vJunction));

    return result;

    //Alternate method.  This also works, but math is not intuitive.
    // let oneMinusTransferFunction = complexOne.subs(complexAmplitude);
    // let denominator = oneMinusTransferFunction.mult(ySum).plus(Complex.complexify(1/r2));
    // let complexR1 = Complex.complexify(r1);
    // let numerator = Complex.complexify(1 + r1/r2).plus(complexR1.mult(ySum));
    // return numerator.divide(denominator);
  }),
  computeMagnitude(complexAlphaMinus, complexFF) {
    let complexAlphaPlus = this.complexAlphaPlus;
    let complexA = this.complexA;
    let amplitudeDenominator = this.amplitudeDenominator;
    let amplitudeNumerator = this._computeAmplitudeNumerator(complexAlphaPlus, complexAlphaMinus, complexA, complexFF);
    let complexAmplitude = this._computeComplexAmplitude(amplitudeNumerator, amplitudeDenominator);
    return complexAmplitude.magnitude();
  },
});
